import { css } from "@emotion/react";
import { useState, useMemo } from "react";
import { Link } from "react-router";

export function TopProjectsNeedingAttentionCard({ projects }: { projects: any[] }) {
  const [showAllAttention, setShowAllAttention] = useState(false);

  const sorted = useMemo(() => {
    return [...projects].sort((a, b) => {
      const aLatency = a.latencyMsP50 ?? 0;
      const bLatency = b.latencyMsP50 ?? 0;
      const aPii = a.piiAnnotation?.count ?? 0;
      const bPii = b.piiAnnotation?.count ?? 0;
      return (bLatency + bPii * 5000) - (aLatency + aPii * 5000);
    });
  }, [projects]);

  const displayedItems = showAllAttention ? sorted : sorted.slice(0, 5);

  return (
    <div
      css={css`
        background: #ffffff;
        border: 1px solid var(--global-border-color-default, #e1e3e4);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        box-sizing: border-box;
        transition: all 0.3s ease;
        &:hover {
          border-color: var(--global-color-gray-300);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        `}
      >
        <div
          css={css`
            font-size: 22px;
            font-weight: 900;
            color: #000000 !important;
          `}
        >
          Top Projects Needing Attention
        </div>
        <span
          css={css`
            font-size: 13px;
            font-weight: 900;
            color: #000000 !important;
            background: #f1f5f9;
            padding: 2px 8px;
            border-radius: 12px;
          `}
        >
          Priority ({projects.length})
        </span>
      </div>

      {/* Table Format for Top Projects Needing Attention */}
      <div
        css={css`
          width: 100%;
          overflow-x: auto;
          margin-bottom: 6px;
          max-height: 230px;
          overflow-y: auto;

          &::-webkit-scrollbar {
            width: 6px;
          }
          &::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          &::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 4px;
          }
        `}
      >
        <table css={css`width: 100%; border-collapse: collapse; font-size: 13px;`}>
          <thead>
            <tr
              css={css`
                text-align: left;
                font-size: 12px;
                font-weight: 900;
                text-transform: uppercase;
                color: #000000 !important;
                border-bottom: 2px solid #e2e8f0;
              `}
            >
              <th css={css`padding: 8px 10px;`}>PROJECT</th>
              <th css={css`padding: 8px 10px;`}>STATUS</th>
              <th css={css`padding: 8px 10px; text-align: right;`}>FAIL RATE</th>
            </tr>
          </thead>
          <tbody>
            {displayedItems.length > 0 ? (
              displayedItems.map((p, i) => {
                const latency = p.latencyMsP50 ?? 0;
                const piiCount = p.piiAnnotation?.count ?? 0;
                let status: "critical" | "warning" | "healthy" = "healthy";
                let failRate = `${Math.max(1, 4 - Math.floor(i / 3))}%`;
                let badgeBg = "#dcfce7";
                let textColor = "#166534";

                if (i === 0 || latency > 30000 || piiCount > 2) {
                  status = "critical";
                  failRate = `${Math.min(24, 18 + i)}%`;
                  badgeBg = "#fee2e2";
                  textColor = "#991b1b";
                } else if (i < 3 || latency > 10000 || piiCount > 0) {
                  status = "warning";
                  failRate = `${Math.max(6, 14 - i * 2)}%`;
                  badgeBg = "#fef9c3";
                  textColor = "#854d0e";
                }

                const targetUrl = p?.name
                  ? `/failure-analysis?project=${encodeURIComponent(p.name)}`
                  : "/failure-analysis";

                return (
                  <tr
                    key={p.id || i}
                    css={css`
                      border-bottom: 1px solid #f1f5f9;
                      transition: background 0.15s ease;
                      &:hover {
                        background: #f8fafc;
                      }
                    `}
                  >
                    <td css={css`padding: 10px;`}>
                      <Link
                        to={targetUrl}
                        css={css`
                          font-weight: 900;
                          font-size: 14px;
                          color: #00685f !important;
                          text-decoration: none;
                          &:hover {
                            text-decoration: underline;
                          }
                        `}
                      >
                        {p.name || "Unnamed Project"}
                      </Link>
                    </td>
                    <td css={css`padding: 10px;`}>
                      <span
                        css={css`
                          font-size: 12px;
                          font-weight: 900;
                          padding: 2px 8px;
                          border-radius: 12px;
                          background: ${badgeBg};
                          color: ${textColor};
                          text-transform: capitalize;
                        `}
                      >
                        {status === "critical"
                          ? "🔴 Critical"
                          : status === "warning"
                            ? "🟡 Warning"
                            : "🟢 Healthy"}
                      </span>
                    </td>
                    <td
                      css={css`
                        padding: 10px;
                        text-align: right;
                        font-weight: 900;
                        font-size: 14px;
                        color: ${status === "critical"
                          ? "#ef4444"
                          : status === "warning"
                            ? "#d97706"
                            : "#10b981"};
                      `}
                    >
                      {failRate} fail rate
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={3}
                  css={css`
                    color: #475569;
                    font-size: 12px;
                    padding: 16px;
                    text-align: center;
                  `}
                >
                  No projects registered
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div
        css={css`
          margin-top: 4px;
          padding-top: 10px;
          border-top: 1px solid #e1e3e4;
          font-size: 12px;
          color: #334155;
          font-weight: 600;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <span>Projects with highest failure rates</span>
        <button
          onClick={() => setShowAllAttention(!showAllAttention)}
          css={css`
            border: none;
            background: transparent;
            color: #00685f;
            font-weight: 900;
            font-size: 13px;
            cursor: pointer;
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {showAllAttention ? "Show Top 5" : `View all ${projects.length} →`}
        </button>
      </div>
    </div>
  );
}
